import * as React from "react";
import { ZSidLabel, zSidLabel } from "src/types";
import { ActivateCallback } from "src/types/ActivateCallback";

import { ImagesListEditor } from "../../../../components/ImagesListEditor/ImagesListEditor";
import { ZTypeDef } from "../../ZAttrMeta";
import { ExternalImagesModal } from "./ExternalImagesModal";
import { KeyedImgFrame } from "./KeyedImgFrame";

/**
 * Элемент редактирования изображений, заданных через внешний урл.
 * Поддерживает типы string и strOption. Как одиночный, так и в виде массива
 */

type PropsAttrExternalImages = {
  attrType: ZTypeDef;
  value: unknown;
  onChange?(newValue: unknown): void;
  disabled?: boolean;
  activateCallback?: ActivateCallback;
  onBlur: () => void;
};

export const AttrExternalImages: React.FC<PropsAttrExternalImages> = (
  props,
) => {
  const { attrType, value, onChange, disabled, activateCallback, onBlur } =
    props;

  const handleChange = (newValue: unknown) => {
    onBlur();
    onChange?.(newValue);
  };

  const typedValue: KeyedImgFrame[] = getTypedValue(value, attrType);

  const [open, setOpen] = React.useState(false);

  const removeItem = (index: number) => {
    if (index >= 0 && index < typedValue.length) {
      const newList = typedValue.map(({ url, label = "" }) => ({
        id: url,
        label,
      }));
      newList.splice(index, 1);
      handleChange(cvtSidsToSourceType(newList, attrType));
    }
  };

  return (
    <>
      <ImagesListEditor
        disabled={disabled}
        items={typedValue}
        activateCallback={activateCallback}
        onRemove={removeItem}
        onOpen={() => setOpen(true)}
      />
      <ExternalImagesModal
        srcValue={typedValue}
        onChange={(newValues) =>
          handleChange(cvtSidsToSourceType(newValues, attrType))
        }
        open={open}
        close={() => setOpen(false)}
        maxCount={
          attrType.name === "array" ? (attrType.maxCount ?? undefined) : 1
        }
      />
    </>
  );
};

const cvtSidsToSourceType = (src: ZSidLabel[], attrType: ZTypeDef) => {
  if (attrType.name === "array") {
    const itemType = attrType.item?.type?.name;
    if (itemType === "strOption") return src;
    if (itemType === "string") return src.map(({ id }) => id);
  } else if (attrType.name === "strOption") return src[0] ?? null;
  else if (attrType.name === "string") return src[0]?.id ?? null;
  return [];
};

const getTypedValue = (value: unknown, attrType: ZTypeDef): KeyedImgFrame[] =>
  cvtToKeyed(getSidLabels(value, attrType));

const cvtToKeyed = (src: ZSidLabel[]): KeyedImgFrame[] => {
  const dict: Record<string, number> = {};
  return src.map(({ id, label }) => {
    const shortKey = `${id}:${label}`;
    const index = (dict[shortKey] ?? 0) + 1;
    dict[shortKey] = index;
    return { key: `${index}:${shortKey}`, url: id, label };
  });
};

const getSidLabels = (value: unknown, attrType: ZTypeDef): ZSidLabel[] => {
  if (!value) return [];
  if (attrType.name === "string" && typeof value === "string") {
    return [{ id: value, label: "" }];
  }
  if (attrType.name === "strOption") {
    const res = zSidLabel.safeParse(value);
    if (res.success) {
      return [res.data];
    }
  }
  if (attrType.name === "array" && Array.isArray(value)) {
    const itemType = attrType?.item?.type;
    if (itemType?.name === "string") {
      return value.map((s) => ({
        label: "",
        id: typeof s === "string" ? s : "",
      }));
    }
    if (itemType?.name === "strOption") {
      return value.map((op) => {
        const res = zSidLabel.safeParse(op);
        return res.success ? res.data : { id: "", label: "" };
      });
    }
  }
  return [];
};
